"use client";

import React, { useEffect, useState } from "react";
import { ShieldCheck, CreditCard, GraduationCap, Globe, Clock, CircleCheck as CheckCircle2, FileText, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface PolicySectionData {
  id: string;
  badge: string;
  title: string;
  summary: string;
  items: Array<{
    heading: string;
    description: string;
  }>;
}
interface LanguageData {
  title: string;
  subtitle: string;
  quickNavTitle: string;
  lastUpdated: string;
  contactPrompt: string;
  contactButton: string;
  supportHours: string;
  privacy: PolicySectionData;
  payment: PolicySectionData;
  admission: PolicySectionData;
}
const contentData: Record<"vi" | "en", LanguageData> = {
  vi: {
    title: "Quy định & chính sách học viện",
    subtitle: "Thông tin minh bạch và rõ ràng về quyền riêng tư của học viên nhí, học phí và thủ tục nhập học tại ROBOTO.",
    quickNavTitle: "Mục lục chính sách",
    lastUpdated: "Cập nhật lần cuối: Tháng 3, 2025",
    contactPrompt: "Quý phụ huynh cần thêm giải thích về các điều khoản hoặc cần hỗ trợ trực tiếp?",
    contactButton: "Liên hệ tư vấn viên",
    supportHours: "Bộ phận tuyển sinh trực tiếp hỗ trợ từ 08:30 đến 18:30 (Thứ Hai - Thứ Bảy)",
    privacy: {
      id: "bao-mat",
      badge: "Chính sách 01",
      title: "Chính sách bảo mật thông tin",
      summary: "Cam kết bảo vệ tuyệt đối dữ liệu học tập và thông tin cá nhân của học sinh trong độ tuổi từ 4 đến 16.",
      items: [{
        heading: "Thu thập dữ liệu cá nhân học sinh",
        description: "Chúng tôi chỉ thu thập họ tên, ngày sinh, trường đang học và tiền sử sức khỏe cơ bản (dị ứng, vấn đề vận động nếu có) nhằm bảo đảm an toàn khi các em thao tác thiết bị cơ khí và robot tại phòng học."
      }, {
        heading: "Hình ảnh và sản phẩm sáng tạo",
        description: "Hình ảnh lớp học hoặc video dự án lập trình của học sinh chỉ được chia sẻ trong nhóm liên lạc gia đình hoặc các kênh truyền thông chính thức sau khi nhận được sự đồng thuận từ phụ huynh."
      }, {
        heading: "Không chia sẻ dữ liệu thương mại",
        description: "Dữ liệu liên lạc của cha mẹ và hồ sơ rèn luyện của học viên không bao giờ được chia sẻ, cho thuê hay bán cho bất kỳ đơn vị tiếp thị bên ngoài nào."
      }, {
        heading: "Quyền truy cập và yêu cầu xóa",
        description: "Phụ huynh có quyền xem lại, điều chỉnh hoặc yêu cầu xóa bỏ toàn bộ dữ liệu định danh của học viên sau khi kết thúc khóa học bằng cách gửi yêu cầu trực tiếp qua ban quản lý."
      }]
    },
    payment: {
      id: "thanh-toan",
      badge: "Chính sách 02",
      title: "Chính sách học phí & thanh toán",
      summary: "Quy định minh bạch về các phương thức thanh toán, hoàn trả và bảo lưu học phí cho học viên.",
      items: [{
        heading: "Phương thức đóng học phí",
        description: "Học viện chấp nhận thanh toán qua chuyển khoản ngân hàng chính thức, cổng thanh toán thẻ điện tử bảo mật hoặc thanh toán trực tiếp tại quầy tuyển sinh trước ngày khai giảng tối thiểu 03 ngày làm việc."
      }, {
        heading: "Bảo lưu buổi học khi học sinh nghỉ có phép",
        description: "Trường hợp học sinh ốm đau hoặc gia đình có lịch đột xuất, phụ huynh thông báo trước ít nhất 12 giờ qua cổng liên lạc để trung tâm sắp xếp lịch học bù cùng giáo viên chuyên trách."
      }, {
        heading: "Chính sách hoàn phí và chuyển nhượng",
        description: "Hoàn 100% học phí nếu phụ huynh thông báo hủy trước ngày khai giảng khóa mới 7 ngày. Sau khi khóa học đã bắt đầu, học phí còn lại có thể chuyển đổi thành tín chỉ bảo lưu tối đa 06 tháng."
      }, {
        heading: "Bộ dụng cụ và tài liệu học tập",
        description: "Chi phí bộ kit LEGO và linh kiện kỹ thuật (nếu chọn mua riêng để luyện tập tại nhà) là chi phí vật tư phi hoàn lại sau khi đã bóc tem niêm phong và bàn giao sử dụng."
      }]
    },
    admission: {
      id: "nhap-hoc",
      badge: "Chính sách 03",
      title: "Chính sách & điều kiện nhập học",
      summary: "Quy trình khảo sát năng lực ban đầu, xếp lớp phù hợp độ tuổi và quy tắc an toàn trong phòng thực hành.",
      items: [{
        heading: "Khảo sát đầu vào và phân lớp theo độ tuổi",
        description: "Mỗi học viên từ 4-16 tuổi đều trải qua buổi đánh giá tư duy tương tác miễn phí cùng huấn luyện viên để xếp lớp đúng cấp độ nhận thức và khả năng tiếp thu logic."
      }, {
        heading: "Quy chuẩn sĩ số và giảng dạy",
        description: "Mỗi lớp học duy trì tỷ lệ tối đa 6 đến 8 học viên trên 1 giảng viên và trợ giảng để bảo đảm hướng dẫn sát sao từng bước lắp ráp phần cứng và viết mã lệnh."
      }, {
        heading: "An toàn phòng thực hành kỹ thuật",
        description: "Học viên được hướng dẫn nghiêm ngặt về an toàn điện áp thấp, bảo quản động cơ servo, cảm biến và tôn trọng không gian sáng tạo của các bạn cùng nhóm."
      }, {
        heading: "Đồng hành và báo cáo học tập",
        description: "Sau mỗi 4 buổi học, phụ huynh nhận báo cáo nhận xét chi tiết gồm các tiêu chí tư duy phản biện, kỹ năng giải quyết lỗi và video sản phẩm sáng tạo do chính học viên thuyết trình."
      }]
    }
  },
  en: {
    title: "Academy terms & policies",
    subtitle: "Clear, transparent standards regarding student privacy, tuition fees, and admission criteria at ROBOTO.",
    quickNavTitle: "Quick navigation",
    lastUpdated: "Last updated: March 2025",
    contactPrompt: "Have questions about our academic policies or need personal assistance?",
    contactButton: "Speak with an advisor",
    supportHours: "Admissions office active Mon - Sat, 08:30 to 18:30",
    privacy: {
      id: "bao-mat",
      badge: "Policy 01",
      title: "Privacy policy",
      summary: "Dedicated to the ethical stewardship and security of minors' educational data (ages 4 to 16).",
      items: [{
        heading: "Student personal information",
        description: "We collect basic identifying data including names, birthdates, grade levels, and relevant health notes to ensure physical safety during robotics assembly and lab sessions."
      }, {
        heading: "Class media and student projects",
        description: "Visual media depicting students and their engineering creations is shared within family channels and official publications only with prior parental consent."
      }, {
        heading: "Zero commercial data sharing",
        description: "Family contact channels and student assessment logs are never sold, rented, or distributed to external marketing providers."
      }, {
        heading: "Data access and removal rights",
        description: "Parents retain full rights to inspect, revise, or permanently delete student profile records upon program completion upon written request."
      }]
    },
    payment: {
      id: "thanh-toan",
      badge: "Policy 02",
      title: "Payment & tuition policy",
      summary: "Simple, transparent guidelines covering course payment methods, absences, and credits.",
      items: [{
        heading: "Tuition collection methods",
        description: "Tuition is settled via verified bank transfers, secure electronic payment gateways, or front-desk counters at least 3 business days before class commencement."
      }, {
        heading: "Excused absences and makeup classes",
        description: "If a student cannot attend due to illness or family matters, notify staff at least 12 hours in advance to schedule a makeup workshop with a mentor."
      }, {
        heading: "Refunds and credit retention",
        description: "Full refund eligibility applies when written notice is given 7 days prior to course start. After commencement, unspent tuition is preserved as learning credits valid for 6 months."
      }, {
        heading: "Hardware kits and lab equipment",
        description: "Custom robotics kits and hardware purchased for home practice are non-refundable once seals are opened and testing protocols conclude."
      }]
    },
    admission: {
      id: "nhap-hoc",
      badge: "Policy 03",
      title: "Admission policy & terms",
      summary: "Initial skill evaluations, class cohort standards, and hardware safety regulations.",
      items: [{
        heading: "Placement assessments",
        description: "Students aged 4 to 16 participate in an initial session to calibrate cognitive readiness, spatial reasoning, and software fundamentals for proper cohort assignment."
      }, {
        heading: "Cohort ratios and guidance",
        description: "We maintain small classroom sizes with a strict maximum of 6 to 8 students per instructor and teaching assistant for attentive personal guidance."
      }, {
        heading: "Laboratory safety standards",
        description: "Students are trained in low-voltage safety, sensor handling, clean workbench habits, and respectful collaboration during group engineering projects."
      }, {
        heading: "Parent progress reports",
        description: "Comprehensive progress reports highlighting logical thinking, debugging agility, and project presentations are delivered every 4 sessions."
      }]
    }
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
export default function Policies() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    // Read initial language safely
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "vi" || stored === "en") {
        setLang(stored);
      }
    } catch {
      // LocalStorage access restricted in some frames
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang: "vi" | "en";
      }>;
      if (customEvent.detail && (customEvent.detail.lang === "vi" || customEvent.detail.lang === "en")) {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange as EventListener);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange as EventListener);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const switchLanguage = (newLang: "vi" | "en") => {
    setLang(newLang);
    try {
      localStorage.setItem("roboto_lang", newLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change", {
        detail: {
          lang: newLang
        }
      }));
    } catch {
      // Ignore storage write issues
    }
  };
  const t = contentData[lang];
  const policySections = [t.privacy, t.payment, t.admission];
  const getSectionIcon = (id: string) => {
    if (id === "bao-mat") return <ShieldCheck className="size-6 text-secondary" />;
    if (id === "thanh-toan") return <CreditCard className="size-6 text-accent" />;
    return <GraduationCap className="size-6 text-primary" />;
  };
  return <section id="academypolicies" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/40">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Top Header Row with Title & Language Switcher */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-secondary/40 text-secondary-foreground font-medium px-3 py-1">
                ROBOTO Academy Standards
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="size-3.5" />
                {t.lastUpdated}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-default">
              {t.title}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
              {t.subtitle}
            </p>
          </div>

          {/* Language Toggle Controls */}
          <div className="flex items-center gap-2 bg-card border border-border p-1.5 rounded-lg shadow-xs self-start md:self-auto">
            <Globe className="size-4 text-muted-foreground ml-2" />
            <div className="flex items-center gap-1">
              <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => switchLanguage("vi")} className="text-xs font-semibold px-3 h-8">
                Tiếng Việt
              </Button>
              <Button variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => switchLanguage("en")} className="text-xs font-semibold px-3 h-8">
                English
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Jump Anchors Row */}
        <div className="py-8">
          <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-xs">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              {t.quickNavTitle}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {policySections.map((sec, i) => <Link key={sec.id} to={`#${sec.id}`} data-index={i} className="flex items-center justify-between p-3.5 rounded-lg bg-background hover:bg-muted border border-border/70 text-foreground transition-[background-color,border-color] group">
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 p-2 rounded-md bg-muted text-foreground">
                      {getSectionIcon(sec.id)}
                    </span>
                    <div className="text-left">
                      <span className="text-xs font-medium text-muted-foreground block">
                        {sec.badge}
                      </span>
                      <span className="text-sm font-semibold block group-hover:text-primary">
                        {sec.title}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-transform" />
                </Link>)}
            </div>
          </div>
        </div>

        {/* Policy Sections List */}
        <div className="space-y-16 pt-4">
          {policySections.map((section, sectionIdx) => <section key={section.id} id={section.id} data-index={sectionIdx} className="scroll-mt-24">
              <Card className="border border-border shadow-sm overflow-hidden">
                <CardHeader className="bg-muted/40 border-b border-border/70 p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2.5 rounded-lg bg-card border border-border shadow-2xs">
                      {getSectionIcon(section.id)}
                    </div>
                    <div>
                      <Badge variant="secondary" className="font-semibold text-xs">
                        {section.badge}
                      </Badge>
                      <CardTitle className="text-2xl sm:text-3xl font-bold mt-1 text-foreground">
                        {section.title}
                      </CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-base text-foreground/80 mt-2">
                    {section.summary}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-6 sm:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {section.items.map((item, itemIdx) => <div key={itemIdx} data-index={itemIdx} className="p-5 rounded-lg bg-background border border-border/60 flex flex-col justify-start">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                          <div>
                            <h3 className="text-base font-semibold text-foreground mb-1.5">
                              {item.heading}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>

                  <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <FileText className="size-4" />
                      <span>{`ROBOTO Document Reference: ROBOTO-POL-${section.id.toUpperCase()}-2025`}</span>
                    </div>
                    <Link to="#academypolicies" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                      Top of page &uarr;
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </section>)}
        </div>

        {/* Academy Contact & Clarification Banner */}
        <div className="mt-16 bg-muted/60 border border-border rounded-xl p-8 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-foreground">
              {t.contactPrompt}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t.supportHours}
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 shadow-sm">
            <Link to="/FreeTrialContactPage">
              {t.contactButton}
            </Link>
          </Button>
        </div>

        {/* Static Decorative Mascot Visual */}
        <div className="mt-8 flex justify-end pointer-events-none select-none relative z-10" aria-hidden="true">
          <div className="w-20 sm:w-24 md:w-28 lg:w-32 pointer-events-none select-none">
            <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" className="w-full h-auto object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
          </div>
        </div>
      </div>
    </section>;
}